import React, {useState, useRef, useEffect} from 'react';

const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16MB

const getFileTypeIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) return 'fa-picture-o';
    if (mimeType.startsWith('audio/')) return 'fa-music';
    if (mimeType.startsWith('video/')) return 'fa-video-camera';
    if (mimeType.includes('pdf')) return 'fa-file-pdf-o';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'fa-file-word-o';
    if (mimeType.includes('excel') || mimeType.includes('sheet')) return 'fa-file-excel-o';
    return 'fa-file-o';
};

const MessageInput = ({ onSendMessage, onSendFile, onOpenTemplateModal, disabled = false }) => {
    const [message, setMessage] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [filePreview, setFilePreview] = useState(null);
    const [caption, setCaption] = useState('');
    const [uploading, setUploading] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [message]);

    // Generar preview cuando se selecciona archivo
    useEffect(() => {
        if (!selectedFile) { setFilePreview(null); return; }
        if (selectedFile.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = (e) => setFilePreview(e.target.result);
            reader.readAsDataURL(selectedFile);
        } else {
            setFilePreview(null);
        }
    }, [selectedFile]);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > MAX_FILE_SIZE) {
            alert('El archivo excede el tamaño máximo de 16MB');
            return;
        }
        setSelectedFile(file);
        setCaption('');
        // Reset input para poder seleccionar el mismo archivo otra vez
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleCancelFile = () => {
        setSelectedFile(null);
        setFilePreview(null);
        setCaption('');
    };

    const handleSendFile = async () => {
        if (!selectedFile || !onSendFile || uploading) return;
        setUploading(true);
        try {
            await onSendFile(selectedFile, caption);
            setSelectedFile(null);
            setFilePreview(null);
            setCaption('');
        } catch (error) {
            alert(`Error al enviar archivo: ${error.message}`);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (selectedFile) {
            handleSendFile();
            return;
        }
        if (message.trim() && !disabled) {
            onSendMessage(message.trim());
            setMessage('');
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <div className="message-input-wrapper">
            {/* Preview de archivo seleccionado */}
            {selectedFile && (
                <div className="file-preview-bar">
                    <div className="file-preview-info">
                        {filePreview ? (
                            <img src={filePreview} alt="Preview" className="file-preview-thumb" />
                        ) : (
                            <div className="file-preview-icon">
                                <i className={`fa ${getFileTypeIcon(selectedFile.type)}`}></i>
                            </div>
                        )}
                        <div className="file-preview-details">
                            <span className="file-preview-name">{selectedFile.name}</span>
                            <span className="file-preview-size">
                                {(selectedFile.size / 1024).toFixed(0)} KB
                            </span>
                        </div>
                        <button
                            type="button"
                            className="file-preview-cancel"
                            onClick={handleCancelFile}
                            title="Cancelar"
                        >
                            <i className="fa fa-times"></i>
                        </button>
                    </div>
                    <input
                        type="text"
                        className="file-caption-input"
                        placeholder="Añadir un comentario..."
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        onKeyPress={handleKeyPress}
                    />
                </div>
            )}

            <form onSubmit={handleSubmit} className="message-input-form">
                {/* Botón plantilla */}
                {onOpenTemplateModal && (
                    <button
                        type="button"
                        className="template-btn"
                        onClick={onOpenTemplateModal}
                        disabled={disabled || uploading}
                        title="Enviar plantilla"
                    >
                        <i className="fa fa-list-alt"></i>
                    </button>
                )}

                {/* Botón adjuntar */}
                <button
                    type="button"
                    className="attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled || uploading}
                    title="Adjuntar archivo"
                >
                    <i className="fa fa-paperclip"></i>
                </button>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />

                {!selectedFile && (
                    <textarea
                        ref={textareaRef}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Escribe un mensaje..."
                        disabled={disabled || uploading}
                        className="message-input"
                        rows={1}
                    />
                )}

                {selectedFile && (
                    <div className="message-input file-selected-label">
                        Archivo listo para enviar
                    </div>
                )}

                <button
                    type="submit"
                    disabled={((!message.trim() && !selectedFile) || disabled || uploading)}
                    className="send-button"
                >
                    {uploading ? (
                        <i className="fa fa-spinner fa-spin"></i>
                    ) : (
                        'Enviar'
                    )}
                </button>
            </form>
        </div>
    );
};

export default MessageInput;